import React from "react";
import PropTypes from "prop-types";
import { ThemeProvider, Typography } from "@theconversation/ui";
import Markdown from "components/Markdown";
import AddressDetails from "components/AddressDetails";
import donationsTheme from "components/donationsTheme";
import GiftAidSection from "components/GiftAidSection";
import FormError from "components/FormError";
import FormFieldset from "components/FormFieldset";
import PubliclyRecogniseDonor from "components/PubliclyRecogniseDonor";
import SubmitButton from "components/SubmitButton";
import ErrorTrack from "lib/ErrorTrack";
import i18n from "lib/i18n";
import { ConfigProps, ConfigDefaultProps } from "components/PropTypes/ConfigProps";

export default class FollowupForm extends React.Component {
  static transformErrors(errors) {
    const transformedErrors = {};
    Object.keys(errors).forEach((key) => {
      const camelizedKey = key.replace(/(_\w)/, match => match.substr(1).toUpperCase());
      transformedErrors[camelizedKey] = new Array(errors[key]).join(", ");
    });
    return transformedErrors;
  }

  constructor(props) {
    super(props);

    this.state = {
      address: this.props.subscription.address || "",
      city: this.props.subscription.city || "",
      country: this.props.subscription.country || "",
      errors: FollowupForm.transformErrors(this.props.subscription.errors || {}),
      postcode: this.props.subscription.postcode || "",
      state: this.props.subscription.state || "",
      giftAid: this.props.subscription.gift_aid || false,
      publiclyRecogniseDonor: !this.props.subscription.anonymous || false,
      submitting: false,
      complete: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onError = this.onError.bind(this);
    this.onLoad = this.onLoad.bind(this);
    this.setAddressDetails = this.setAddressDetails.bind(this);
    this.setRecogniseDonor = this.setRecogniseDonor.bind(this);
  }

  onError(e) {
    this.setState({ submitting: false });
    ErrorTrack.notify(e);
    this.unclassifiedError(e);
  }

  onLoad(e) {
    this.setState({ submitting: false });
    try {
      const response = JSON.parse(e.target.responseText);
      if (response.success) {
        this.setState({ complete: true });
      } else {
        this.setState({ errors: FollowupForm.transformErrors(response.errors) });
      }
    } catch (error) {
      ErrorTrack.notify(error);
      this.unclassifiedError(e);
    }
  }

  setAddressDetails(addressDetails) {
    this.setState(addressDetails);
  }

  setRecogniseDonor() {
    this.setAddressDetails({ publiclyRecogniseDonor: !this.state.publiclyRecogniseDonor });
  }

  unclassifiedError() {
    this.setState({ errors: { base: [i18n.t("capture_address.message.unrecoverable_error")] } });
  }

  handleSubmit(e) {
    e.preventDefault();
    this.setState({ errors: {} });
    const data = new FormData(this.form);
    const subscriptionId = data.get("subscription[subscription_id]");
    this.setState({ submitting: true }, () => {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener("load", this.onLoad);
      xhr.addEventListener("error", this.onError);
      xhr.open("PUT", this.props.url.replace(":id", subscriptionId));
      xhr.send(data);
    });
  }

  renderForm() {
    if (this.state.complete) {
      return null;
    }

    const giftAidSection = this.props.config.locale === "en-GB" ? (
      <GiftAidSection
        active={!this.state.submitting}
        giftAid={this.state.giftAid}
        onChange={this.setAddressDetails}
        formName="subscription"
      />
    ) : "";

    return (
      <form
        acceptCharset="UTF-8"
        action={this.props.url}
        id="followup-form"
        method="post"
        onSubmit={this.handleSubmit}
        ref={(component) => { this.form = component; }}
      >
        <input name="utf8" type="hidden" value="✓" />
        <input name="authenticity_token" type="hidden" value={this.props.authenticityToken} />
        <input name="subscription[auth_token]" type="hidden" value={this.props.authToken} />
        <input
          name="subscription[subscription_id]"
          type="hidden"
          value={this.props.subscription.id}
        />
        <input name="subscription[followup_form]" type="hidden" value="1" />
        {giftAidSection}
        <fieldset>
          <FormFieldset>
            <Typography variant="h6">{i18n.t("capture_address.title")}</Typography>
          </FormFieldset>
          {i18n.t("capture_address.blurb") !== "" &&
            <p>{i18n.t("capture_address.blurb")}</p>
          }
          <FormError error={this.state.errors.base} />

          <AddressDetails
            address={this.state.address}
            city={this.state.city}
            country={this.state.country}
            errors={this.state.errors}
            homeCountryCode={this.props.config.region_home_country_code}
            postcode={this.state.postcode}
            state={this.state.state}
            objectName="subscription"
            onChange={this.setAddressDetails}
          />
        </fieldset>

        <FormFieldset>
          <PubliclyRecogniseDonor
            active={!this.state.submitting}
            onChange={this.setRecogniseDonor}
            publiclyRecogniseDonor={this.state.publiclyRecogniseDonor}
            formName="subscription"
          />
        </FormFieldset>

        <SubmitButton
          active={!this.state.submitting}
          text={i18n.t("capture_address.form.update")}
        />
      </form>
    );
  }

  renderSuccessMessage() {
    if (this.state.complete) {
      const date = new Date();
      const month = date.toLocaleString(undefined, { month: "short" });
      const name = `${this.props.subscription.name} ${this.props.subscription.last_name}`;
      const email = i18n.t("capture_address.success.email");
      const onceOff = this.props.subscription.frequency === "once";
      const advice = onceOff ?
        i18n.t("capture_address.success.receipt_once") :
        i18n.t("capture_address.success.receipt_monthly", { email });

      return (
        <Markdown>
          {
            i18n.t(
              "capture_address.success.thank_you_message",
              {
                date: `${month}. ${date.getDate()}, ${date.getFullYear()}`,
                name,
                receipt_advice: advice,
              }
            )
          }
        </Markdown>
      );
    }

    return null;
  }

  render() {
    return (
      <ThemeProvider theme={donationsTheme}>
        {this.renderForm()}
        {this.renderSuccessMessage()}
      </ThemeProvider>
    );
  }
}

FollowupForm.defaultProps = {
  authenticityToken: "",
  authToken: "",
  config: ConfigDefaultProps,
  subscription: {
    errors: {},
  },
  url: "",
};

FollowupForm.propTypes = {
  authenticityToken: PropTypes.string,
  authToken: PropTypes.string,
  config: ConfigProps,
  subscription: PropTypes.shape({
    address: PropTypes.string,
    anonymous: PropTypes.bool,
    city: PropTypes.string,
    country: PropTypes.string,
    errors: PropTypes.object,
    frequency: PropTypes.string,
    id: PropTypes.number,
    last_name: PropTypes.string,
    name: PropTypes.string,
    postcode: PropTypes.string,
    state: PropTypes.string,
    gift_aid: PropTypes.bool,
  }),
  url: PropTypes.string,
};
